import { BasicValue } from '@cutr/constants/windows/windows-configurations.ts';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { onImgError } from '@/blocks/materialRowUtils';
import { Modal } from '@/blocks/Modal';
import { Button } from '@/primitives/Button';
import { Edit, Icon } from '@/primitives/Icons';
import { useToggle } from '@/utils/hooks';

import styles from '../../GridSelectModal.module.css';
import { unboxLabel } from '../Configurations';
import { ConfigurationButton } from './ConfigurationButton';

export type LocalGridSelectModalProps = {
  modalTitle: string;
  modalDescription: string;
  options: { config: BasicValue; imagePath: string }[];
  value: BasicValue | null;
  onChange: (value: BasicValue) => void;
  buttonId?: string;
  buttonLabel?: string;
  showSelectedImage?: boolean;
  columnsCount?: number;
};

export const LocalGridSelectModal = ({
  modalTitle,
  modalDescription,
  options,
  value,
  onChange,
  buttonId,
  buttonLabel,
  showSelectedImage = false,
  columnsCount,
}: LocalGridSelectModalProps) => {
  const [isOpen, toggleModal] = useToggle(false);

  const label = buttonLabel ?? unboxLabel(value?.label);
  const selectedImagePath = options.find(
    (option) => option.config.value === value?.value
  )?.imagePath;
  return (
    <>
      <div>
        <ConfigurationButton
          label={label}
          onClick={toggleModal}
          icon={<Icon icon={<Edit />} size={2.5} />}
          imagePath={showSelectedImage ? selectedImagePath : undefined}
          id={buttonId}
        />
      </div>
      {isOpen && (
        <Modal title={modalTitle} isOpen={isOpen} handleClose={toggleModal}>
          <ModalInner
            toggleModal={toggleModal}
            options={options}
            description={modalDescription}
            selectedValue={value?.value ?? null}
            onChange={onChange}
            columnsCount={columnsCount}
          />
        </Modal>
      )}
    </>
  );
};

type ModalInnerProps = {
  toggleModal: () => void;
  options: LocalGridSelectModalProps['options'];
  description: string;
  selectedValue: string | null;
  onChange: (value: BasicValue) => void;
  columnsCount?: number;
};

const ModalInner = ({
  toggleModal,
  options,
  description,
  selectedValue,
  onChange,
  columnsCount = 3,
}: ModalInnerProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<string | null>(selectedValue);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const picked = options.find((option) => option.config.value === selected);
    if (!picked) return;
    onChange(picked.config);
    toggleModal();
  };

  return (
    <>
      <p>{description}</p>
      <form onSubmit={onSubmit} className="stack">
        <div
          className={styles.modalLayout}
          style={{ gridTemplateColumns: `repeat(${columnsCount}, 1fr)` }}
        >
          {options.map((option) => (
            <GridItem
              key={option.config.value}
              option={option}
              onClick={setSelected}
              selected={selected === option.config.value}
            />
          ))}
        </div>
        <Button type="submit">{t('common.cta.save')}</Button>
      </form>
    </>
  );
};

type GridItemProps = {
  option: LocalGridSelectModalProps['options'][number];
  onClick: (selected: string) => void;
  selected: boolean;
};

const GridItem = ({ option, onClick, selected }: GridItemProps) => {
  const optionValue = option.config;
  const imagePath = option.imagePath;
  const [loaded, setLoaded] = React.useState(false);

  return (
    <div
      className={cn(styles.gridItem, selected && styles.selected)}
      onClick={() => onClick(optionValue.value)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(optionValue.value);
        }
      }}
      aria-label={unboxLabel(optionValue.label)}
    >
      <div className={cn(styles.imageWrapper, !loaded && styles.skeleton)}>
        <img
          src={imagePath}
          alt={unboxLabel(optionValue.label)}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            setLoaded(true);
            onImgError(e);
          }}
          data-loaded={loaded}
        />
      </div>
      <strong className={styles.gridItemLabel}>
        {unboxLabel(optionValue.label)}
      </strong>
    </div>
  );
};
